import { useState } from 'react';
import { Typography, Avatar, Button, Tooltip, Modal, List, Input, message as antMessage } from 'antd';
import { RobotOutlined, CodeOutlined, BarChartOutlined, TableOutlined, ExpandOutlined, AppstoreAddOutlined, PlusOutlined, CopyOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { format } from 'sql-formatter';
import { QueryMessage, StatData, ChartData } from '@/types/query';
import { ChartRenderer, StatCardRenderer, TableRenderer } from './ChartRenderer';
import dashboardService from '@/services/dashboardService';
import { Dashboard } from '@/types/dashboard';

const { Text } = Typography;

interface QueryResultCardProps {
  message: QueryMessage;
}

type ViewMode = 'chart' | 'table' | 'sql';

export const QueryResultCard = ({ message }: QueryResultCardProps) => {
  // For AI-requested table type, start in table mode; otherwise start in chart mode
  const isTableType = message.chartData?.type === 'table';
  const [viewMode, setViewMode] = useState<ViewMode>(isTableType ? 'table' : 'chart');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDashboardModalOpen, setIsDashboardModalOpen] = useState(false);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newDashboardTitle, setNewDashboardTitle] = useState('');
  const [loadingDashboards, setLoadingDashboards] = useState(false);

  // Load dashboards when modal opens
  const handleOpenDashboardModal = async () => {
    setIsDashboardModalOpen(true);
    setLoadingDashboards(true);
    try {
      const response = await dashboardService.getDashboards();
      if (response.success) {
        setDashboards(response.dashboards);
      }
    } catch (error) {
      console.error('Failed to load dashboards:', error);
      antMessage.error('Failed to load dashboards');
    } finally {
      setLoadingDashboards(false);
    }
  };

  // Create new dashboard
  const handleCreateNewDashboard = async () => {
    if (!newDashboardTitle.trim()) {
      antMessage.error('Please enter a dashboard title');
      return;
    }

    try {
      const response = await dashboardService.createDashboard(newDashboardTitle.trim());
      if (response.success) {
        antMessage.success('Dashboard created');
        setNewDashboardTitle('');
        setIsCreatingNew(false);
        // Refresh dashboards list
        const listResponse = await dashboardService.getDashboards();
        if (listResponse.success) {
          setDashboards(listResponse.dashboards);
        }
      }
    } catch (error) {
      console.error('Failed to create dashboard:', error);
      antMessage.error('Failed to create dashboard');
    }
  };

  // Add widget to dashboard
  const handleAddToDashboard = async (dashboardId: string, dashboardTitle: string) => {
    const chartData = message.chartData;
    const statData = message.statData;
    const sql = message.sql;
    const rowCount = message.rowCount;

    if (!chartData && !statData) {
      antMessage.error('No chart or stat data to add');
      return;
    }

    try {
      await dashboardService.addWidget(dashboardId, {
        title: chartData?.title || statData?.label || 'Untitled Widget',
        widgetConfig: {
          chartData,
          statData,
          sql,
          rowCount
        },
        layout: { x: 0, y: 0, w: 6, h: 5 }, // Default layout
        sourceMessageId: message.id
      });

      antMessage.success(`Added to ${dashboardTitle}`);
      setIsDashboardModalOpen(false);
    } catch (error) {
      console.error('Failed to add widget:', error);
      antMessage.error('Failed to add widget to dashboard');
    }
  };

  const renderStatCard = (statData: StatData) => {
    const showSQL = viewMode === 'sql';

    const handleCopySQL = () => {
      if (message.sql) {
        navigator.clipboard.writeText(format(message.sql, { language: 'postgresql' }));
        antMessage.success('SQL copied to clipboard');
      }
    };

    return (
      <>
        <div className="mt-4 p-6 bg-white border border-gray-200 rounded-xl relative group">
          {/* Hover buttons */}
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 z-10">
            <Tooltip title="Add to Dashboard">
              <Button
                type="text"
                size="small"
                icon={<AppstoreAddOutlined />}
                onClick={handleOpenDashboardModal}
                className="bg-white shadow-sm hover:shadow-md"
              />
            </Tooltip>
            <Tooltip title={showSQL ? "Show Stat" : "Show SQL"}>
              <Button
                type="text"
                size="small"
                icon={showSQL ? <BarChartOutlined /> : <CodeOutlined />}
                onClick={() => setViewMode(showSQL ? 'chart' : 'sql')}
                className="bg-white shadow-sm hover:shadow-md"
              />
            </Tooltip>
          </div>
          
          {/* Content */}
          {showSQL && message.sql ? (
            <div className="bg-gray-900 text-gray-100 rounded-lg overflow-auto p-4 relative">
              <Tooltip title="Copy SQL">
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={handleCopySQL}
                  className="text-gray-400 hover:text-white"
                  style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10, color: '#9CA3AF' }}
                />
              </Tooltip>
              <pre className="text-sm whitespace-pre-wrap">
                <code>{format(message.sql, { language: 'postgresql' })}</code>
              </pre>
            </div>
          ) : (
            <StatCardRenderer statData={statData} />
          )}
        </div>
      </>
    );
  };

  const renderChart = (chartData: ChartData) => {
    if (!chartData || !chartData.data || chartData.data.length === 0) return null;

    // AI-requested table type: only toggles between table and SQL (no chart view)
    const isAITable = chartData.type === 'table';

    const handleCopySQL = () => {
      if (message.sql) {
        navigator.clipboard.writeText(format(message.sql, { language: 'postgresql' }));
        antMessage.success('SQL copied to clipboard');
      }
    };

    const renderContent = () => {
      if (viewMode === 'sql') {
        return (
          <div className="bg-gray-900 text-gray-100 rounded overflow-auto relative" style={{ height: 300 }}>
            <Tooltip title="Copy SQL">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={handleCopySQL}
                className="text-gray-400 hover:text-white"
                style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10, color: '#9CA3AF' }}
              />
            </Tooltip>
            <pre className="p-4 text-sm whitespace-pre-wrap">
              <code>{format(message.sql || '', { language: 'postgresql' })}</code>
            </pre>
          </div>
        );
      }
      if (viewMode === 'table') {
        return (
          <div className="overflow-auto" style={{ height: 300 }}>
            <TableRenderer data={chartData.data} chartData={chartData} />
          </div>
        );
      }
      // viewMode === 'chart'
      return <ChartRenderer chartData={chartData} height={300} />;
    };

    return (
      <div className="mt-4 p-5 bg-gray-50 rounded-lg border border-gray-200 relative group">
        {/* Hover buttons */}
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2 z-10">
          <Tooltip title="Add to Dashboard">
            <Button
              type="text"
              size="small"
              icon={<AppstoreAddOutlined />}
              onClick={handleOpenDashboardModal}
              className="bg-white shadow-sm hover:shadow-md"
            />
          </Tooltip>
          {/* Expand - in chart or table mode */}
          {viewMode !== 'sql' && (
            <Tooltip title="Expand">
              <Button
                type="text"
                size="small"
                icon={<ExpandOutlined />}
                onClick={() => setIsExpanded(true)}
                className="bg-white shadow-sm hover:shadow-md"
              />
            </Tooltip>
          )}
          {/* View toggle buttons */}
          {viewMode === 'chart' && (
            <Tooltip title="Show Table">
              <Button
                type="text"
                size="small"
                icon={<TableOutlined />}
                onClick={() => setViewMode('table')}
                className="bg-white shadow-sm hover:shadow-md"
              />
            </Tooltip>
          )}
          {viewMode === 'table' && (
            <>
              <Tooltip title="Show SQL">
                <Button
                  type="text"
                  size="small"
                  icon={<CodeOutlined />}
                  onClick={() => setViewMode('sql')}
                  className="bg-white shadow-sm hover:shadow-md"
                />
              </Tooltip>
              {!isAITable && (
                <Tooltip title="Show Chart">
                  <Button
                    type="text"
                    size="small"
                    icon={<BarChartOutlined />}
                    onClick={() => setViewMode('chart')}
                    className="bg-white shadow-sm hover:shadow-md"
                  />
                </Tooltip>
              )}
            </>
          )}
          {viewMode === 'sql' && (
            <Tooltip title="Show Table">
              <Button
                type="text"
                size="small"
                icon={<TableOutlined />}
                onClick={() => setViewMode('table')}
                className="bg-white shadow-sm hover:shadow-md"
              />
            </Tooltip>
          )}
        </div>

        {chartData.title && (
          <Text strong className="block text-base mb-4 text-gray-800">
            {chartData.title}
          </Text>
        )}

        {renderContent()}
      </div>
    );
  };

  return (
    <div className="mt-6 w-full p-6">
      <div className="flex items-center gap-3 mb-4">
        <Avatar
          size={36}
          className="bg-secondary-500 shrink-0"
          icon={<RobotOutlined />}
        />
        <Text strong className="text-base text-gray-800">
          Beemi Admin
        </Text>
        <Text type="secondary" className="text-sm text-gray-400">
          {new Date(message.timestamp).toLocaleTimeString()}
        </Text>
      </div>
      
      <div className="pl-12">
        <div className="text-base text-gray-800 leading-relaxed mb-4 prose prose-sm max-w-none">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              p: ({ children }) => <p className="whitespace-pre-wrap mb-2">{children}</p>,
              ul: ({ children }) => <ul className="list-disc ml-4 mb-2">{children}</ul>,
              ol: ({ children }) => <ol className="list-decimal ml-4 mb-2">{children}</ol>,
              li: ({ children }) => <li className="mb-1">{children}</li>,
              code: ({ inline, children, ...props }: any) => 
                inline ? (
                  <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono" {...props}>
                    {children}
                  </code>
                ) : (
                  <code className="block bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto text-sm font-mono my-2" {...props}>
                    {children}
                  </code>
                ),
              strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
              em: ({ children }) => <em className="italic">{children}</em>,
              h1: ({ children }) => <h1 className="text-xl font-bold mb-2">{children}</h1>,
              h2: ({ children }) => <h2 className="text-lg font-bold mb-2">{children}</h2>,
              h3: ({ children }) => <h3 className="text-base font-bold mb-1">{children}</h3>,
            }}
          >
            {message.content}
          </ReactMarkdown>
        </div>
        
        {/* No results message */}
        {message.sql && message.rowCount === 0 && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-sm">
              📊 No data found for this time period.
            </p>
          </div>
        )}
        
        {/* Stat Card */}
        {message.statData && renderStatCard(message.statData)}
        
        {/* Chart */}
        {message.chartData && renderChart(message.chartData)}
      </div>

      {/* Expanded Chart / Table Modal */}
      {message.chartData && (
        <Modal
          title={message.chartData.title || (viewMode === 'table' ? "Table Preview" : "Chart Preview")}
          open={isExpanded}
          onCancel={() => setIsExpanded(false)}
          width="90%"
          footer={null}
          centered
        >
          <div className="p-4">
            {viewMode === 'table' ? (
              <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
                <TableRenderer data={message.chartData.data} chartData={message.chartData} />
              </div>
            ) : (
              <ChartRenderer chartData={message.chartData} height={600} />
            )}
          </div>
        </Modal>
      )}

      {/* Add to Dashboard Modal */}
      <Modal
        title="Add to Dashboard"
        open={isDashboardModalOpen}
        onCancel={() => {
          setIsDashboardModalOpen(false);
          setIsCreatingNew(false);
          setNewDashboardTitle('');
        }}
        footer={null}
        width={500}
      >
        {isCreatingNew ? (
          <div className="space-y-4">
            <Input
              placeholder="Enter dashboard title"
              value={newDashboardTitle}
              onChange={(e) => setNewDashboardTitle(e.target.value)}
              onPressEnter={handleCreateNewDashboard}
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <Button onClick={() => {
                setIsCreatingNew(false);
                setNewDashboardTitle('');
              }}>
                Cancel
              </Button>
              <Button type="primary" onClick={handleCreateNewDashboard}>
                Create
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => setIsCreatingNew(true)}
              className="w-full mb-4"
            >
              Create New Dashboard
            </Button>
            <List
              loading={loadingDashboards}
              dataSource={dashboards}
              renderItem={(dashboard) => (
                <List.Item
                  className="cursor-pointer hover:bg-gray-50 px-4 rounded"
                  onClick={() => handleAddToDashboard(dashboard.id, dashboard.title)}
                >
                  <Text>{dashboard.title}</Text>
                </List.Item>
              )}
              locale={{ emptyText: 'No dashboards found. Create one to get started.' }}
            />
          </>
        )}
      </Modal>
    </div>
  );
};
